import type { StateCreator } from 'zustand'
import type { ToolCall, LogEntry } from '../../types'
import type { GlobalEventEnvelope } from '@/shared/lib/api/sse'

export interface EnrichmentSlice {
  // Set of workflow IDs currently being enriched
  enrichingWorkflows: Set<number>
  // Logs from enrichment process (keyed by workflow ID in the log)
  enrichmentLogs: LogEntry[]
  // Tool calls made during enrichment
  enrichmentToolCalls: ToolCall[]
  // Actions
  addEnrichmentEvent: (workflowId: number, event: EnrichmentEvent) => void
  clearEnrichmentLogs: (workflowId: number) => void
  setEnrichingWorkflow: (workflowId: number, enriching: boolean) => void
}

export interface EnrichmentEvent {
  type: 'init' | 'output' | 'tool_use' | 'tool_result' | 'error' | 'complete'
  content: string
  data?: Record<string, unknown>
}

export const createEnrichmentSlice: StateCreator<
  EnrichmentSlice,
  [],
  [],
  EnrichmentSlice
> = (set, get) => ({
  enrichingWorkflows: new Set(),
  enrichmentLogs: [],
  enrichmentToolCalls: [],

  addEnrichmentEvent: (workflowId: number, event: EnrichmentEvent) => {
    const state = get()

    // Create a log entry from the event
    const logEntry: LogEntry = {
      id: `enrich-log-${Date.now()}-${Math.random()}`,
      timestamp: new Date().toISOString(),
      agentId: (event.data?.agent as string) || 'enrichment',
      workflowId,
      level: event.type === 'error' ? 'ERROR' : event.type === 'complete' ? 'INFO' : 'DEBUG',
      message: event.content || '',
    }

    // Add log entry
    set({ enrichmentLogs: [...state.enrichmentLogs, logEntry] })

    // Handle tool_use events
    if (event.type === 'tool_use' && event.data) {
      const data = event.data
      const toolCall: ToolCall = {
        id: `enrich-tool-${Date.now()}-${Math.random()}`,
        agentId: (data.agent as string) || 'enrichment',
        toolId: (data.tool as string) || 'unknown',
        workflowId,
        executorName: 'enrichment',
        timestamp: new Date().toISOString(),
        status: 'RUNNING',
        durationMs: 0,
        args: (data.input as Record<string, unknown>) || {},
        result: null,
        tool_use_id: data.tool_use_id as string | undefined,
      }
      set({ enrichmentToolCalls: [...state.enrichmentToolCalls, toolCall] })
    }

    // Handle complete or error — mark workflow as no longer enriching
    if (event.type === 'complete' || event.type === 'error') {
      const updated = new Set(state.enrichingWorkflows)
      updated.delete(workflowId)
      set({ enrichingWorkflows: updated })
    }
  },

  clearEnrichmentLogs: (workflowId: number) => {
    const state = get()
    set({
      enrichmentLogs: state.enrichmentLogs.filter(l => l.workflowId !== workflowId),
      enrichmentToolCalls: state.enrichmentToolCalls.filter(tc => tc.workflowId !== workflowId),
    })
  },

  setEnrichingWorkflow: (workflowId: number, enriching: boolean) => {
    const state = get()
    const updated = new Set(state.enrichingWorkflows)
    if (enriching) {
      updated.add(workflowId)
    } else {
      updated.delete(workflowId)
    }
    set({ enrichingWorkflows: updated })
  },
})

// ─────────────────────────────────────────────────────────────────────────────
// Global bus dispatcher for enrichment events
// Called by useGlobalEventStream for envelope.source === 'enrichment'
// ─────────────────────────────────────────────────────────────────────────────

export function dispatchEnrichmentEvent(envelope: GlobalEventEnvelope): void {
  // Lazy import to avoid circular dependency at module init time
  import('@/features/orchestration/store/orchestrationStore').then(({ useOrchestrationStore }) => {
    const state = useOrchestrationStore.getState()
    const workflowId = envelope.source_id as number

    // Mark workflow as enriching on init event
    if (envelope.type === 'init') {
      state.setEnrichingWorkflow(workflowId, true)
    }

    // Build enrichment event from envelope
    const enrichmentEvent: EnrichmentEvent = {
      type: envelope.type as EnrichmentEvent['type'],
      content: (envelope.content as string) || '',
      data: envelope.data as Record<string, unknown> | undefined,
    }

    state.addEnrichmentEvent(workflowId, enrichmentEvent)

    // Refresh workflows on complete to get updated status
    if (envelope.type === 'complete') {
      state.refreshWorkflows()
    }
  }).catch(() => {})
}
